import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../styles/design-system.js';
import { iconStyles } from '../styles/icon-styles.js';

export class AnalyticsDashboard extends LitElement {
  static properties = {
    timeRange: { type: String, state: true },
    agentFilter: { type: String, state: true },
    hoveredBar: { type: Number, state: true },
    hoveredHeatCell: { type: Object, state: true },
  };

  static styles = [
    designSystem,
    componentStyles,
    css`
      :host {
        display: block;
        font-family: var(--font-body);
        color: var(--color-text-primary);
      }

      .dashboard-layout {
        display: flex;
        flex-direction: column;
        gap: var(--space-8);
        animation: fadeIn var(--duration-slow) var(--ease-out) both;
      }

      /* ─── Time Range Selector Row ─── */
      .controls-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-4);
        flex-wrap: wrap;
      }

      .controls-left {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .time-pills {
        display: flex;
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        padding: 3px;
        gap: 2px;
      }

      .time-pill {
        padding: var(--space-1) var(--space-4);
        border: none;
        border-radius: var(--radius-sm);
        background: transparent;
        color: var(--color-text-secondary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        cursor: pointer;
        transition: all var(--transition-fast);
        white-space: nowrap;
      }

      .time-pill:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
      }

      .time-pill.active {
        background: var(--color-accent);
        color: var(--color-text-inverse);
        box-shadow: var(--shadow-sm);
      }

      .agent-select {
        padding: var(--space-2) var(--space-3);
        padding-right: var(--space-8);
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-primary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23A8A29E' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 10px center;
      }

      .agent-select:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
      }

      .refresh-btn {
        width: 36px;
        height: 36px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        padding: 0;
        font-size: var(--text-sm);
      }

      .refresh-btn:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-primary);
        border-color: var(--color-border-secondary);
      }

      /* ─── 6 KPI Cards ─── */
      .kpi-grid {
        display: grid;
        grid-template-columns: repeat(6, 1fr);
        gap: var(--space-3);
      }

      .kpi-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        transition: all var(--transition-base);
        position: relative;
        overflow: hidden;
      }

      .kpi-card:hover {
        box-shadow: var(--shadow-hover-lift);
        border-color: var(--color-border-secondary);
        transform: scale(1.01);
      }

      .kpi-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: var(--kpi-accent, var(--color-primary));
      }

      .kpi-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .kpi-icon {
        width: 48px;
        height: 48px;
        border-radius: var(--radius-lg);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-base);
        flex-shrink: 0;
        background: linear-gradient(135deg, var(--kpi-bg, var(--color-primary-light)), var(--kpi-accent, var(--color-primary)));
        color: #fff;
      }

      .kpi-trend {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        padding: 1px var(--space-2);
        border-radius: var(--radius-full);
      }

      .kpi-trend.up {
        background: var(--color-success-bg);
        color: var(--color-success-text);
      }

      .kpi-trend.down {
        background: var(--color-error-bg);
        color: var(--color-error-text);
      }

      .kpi-value {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        line-height: var(--leading-tight);
        letter-spacing: var(--tracking-tighter);
      }

      .kpi-label {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        font-weight: var(--font-medium);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
      }

      /* CSS-only sparkline: 7 thin bars */
      .kpi-sparkline {
        display: flex;
        align-items: flex-end;
        gap: 2px;
        height: 24px;
        margin-top: var(--space-1);
      }

      .spark-bar {
        flex: 1;
        border-radius: 1px;
        background: var(--kpi-accent, var(--color-primary));
        opacity: 0.35;
        transition: opacity var(--transition-fast);
      }

      .kpi-card:hover .spark-bar {
        opacity: 0.55;
      }

      /* ─── Call Volume Trend Chart ─── */
      .section-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-4);
      }

      .section-title-group {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .section-title {
        font-family: var(--font-display);
        font-size: var(--text-lg);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
      }

      .section-subtitle {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      /* Bar chart */
      .bar-chart {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .bar-chart-area {
        display: flex;
        align-items: flex-end;
        gap: 3px;
        height: 200px;
        padding: 0 var(--space-1);
        position: relative;
      }

      /* Y-axis labels */
      .bar-chart-y-axis {
        position: absolute;
        left: -4px;
        top: 0;
        bottom: 0;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        pointer-events: none;
      }

      .y-label {
        font-family: var(--font-mono);
        font-size: 9px;
        color: var(--color-text-disabled);
        text-align: right;
        width: 28px;
        transform: translateY(4px);
      }

      .bar-column {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        cursor: pointer;
      }

      .bar-fill {
        width: 100%;
        max-width: 28px;
        border-radius: 3px 3px 0 0;
        background: linear-gradient(180deg, var(--color-primary), rgba(45, 143, 111, 0.6));
        transition: all var(--transition-fast);
        position: relative;
      }

      .bar-column:hover .bar-fill {
        background: linear-gradient(180deg, var(--color-primary-hover), var(--color-primary));
        box-shadow: 0 -2px 8px rgba(45, 143, 111, 0.25);
      }

      .bar-tooltip {
        position: absolute;
        bottom: calc(100% + 6px);
        left: 50%;
        transform: translateX(-50%);
        background: var(--color-text-primary);
        color: var(--color-text-inverse);
        padding: 3px 8px;
        border-radius: var(--radius-sm);
        font-family: var(--font-mono);
        font-size: 10px;
        white-space: nowrap;
        pointer-events: none;
        z-index: var(--z-tooltip);
        opacity: 0;
        transition: opacity var(--transition-fast);
      }

      .bar-column:hover .bar-tooltip {
        opacity: 1;
      }

      .bar-chart-x-axis {
        display: flex;
        gap: 3px;
        padding: 0 var(--space-1);
      }

      .x-label {
        flex: 1;
        text-align: center;
        font-family: var(--font-mono);
        font-size: 9px;
        color: var(--color-text-disabled);
      }

      .x-label.highlight {
        color: var(--color-text-tertiary);
        font-weight: var(--font-semibold);
      }

      /* ─── Two-column Layout ─── */
      .two-col {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--space-8);
      }

      /* ─── Conversion Funnel ─── */
      .funnel-stages {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }

      .funnel-stage {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .funnel-bar-wrap {
        flex: 1;
        position: relative;
      }

      .funnel-bar {
        height: 44px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 var(--space-3);
        transition: all var(--transition-base);
        background: linear-gradient(90deg, var(--color-primary), rgba(45, 143, 111, 0.65));
        position: relative;
        overflow: hidden;
      }

      .funnel-bar::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(90deg, transparent 70%, rgba(255,255,255,0.15));
      }

      .funnel-bar:hover {
        transform: translateX(3px);
        box-shadow: var(--shadow-md);
      }

      .funnel-stage-label {
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-inverse);
        z-index: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .funnel-stage-count {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-bold);
        color: var(--color-text-inverse);
        z-index: 1;
      }

      .funnel-pct {
        min-width: 50px;
        text-align: right;
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
      }

      /* Connector arrows */
      .funnel-connector {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 12px;
        color: var(--color-text-disabled);
        font-size: 10px;
      }

      /* ─── Peak Hours Heatmap ─── */
      .heatmap-container {
        overflow-x: auto;
      }

      .heatmap-grid {
        display: grid;
        grid-template-columns: 40px repeat(24, 1fr);
        gap: 2px;
        min-width: 500px;
      }

      .heatmap-row-label {
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        color: var(--color-text-tertiary);
        display: flex;
        align-items: center;
        padding-right: var(--space-2);
      }

      .heatmap-col-header {
        font-family: var(--font-mono);
        font-size: 8px;
        color: var(--color-text-disabled);
        text-align: center;
        padding-bottom: 2px;
      }

      .heatmap-corner {
        /* empty top-left corner */
      }

      .heat-cell {
        aspect-ratio: 1;
        border-radius: 3px;
        cursor: pointer;
        transition: all var(--transition-fast);
        position: relative;
      }

      .heat-cell:hover {
        transform: scale(1.15);
        z-index: 5;
        box-shadow: var(--shadow-md);
      }

      /* Intensity levels using primary color */
      .heat-cell.i0 { background: var(--color-bg-tertiary); }
      .heat-cell.i1 { background: rgba(45, 143, 111, 0.12); }
      .heat-cell.i2 { background: rgba(45, 143, 111, 0.28); }
      .heat-cell.i3 { background: rgba(45, 143, 111, 0.45); }
      .heat-cell.i4 { background: rgba(45, 143, 111, 0.65); }
      .heat-cell.i5 { background: rgba(45, 143, 111, 0.85); }

      .heat-cell-tooltip {
        position: absolute;
        bottom: calc(100% + 4px);
        left: 50%;
        transform: translateX(-50%);
        background: var(--color-text-primary);
        color: var(--color-text-inverse);
        padding: 2px 6px;
        border-radius: var(--radius-sm);
        font-family: var(--font-mono);
        font-size: 9px;
        white-space: nowrap;
        pointer-events: none;
        z-index: var(--z-tooltip);
        opacity: 0;
        transition: opacity var(--transition-fast);
      }

      .heat-cell:hover .heat-cell-tooltip {
        opacity: 1;
      }

      .heatmap-legend {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-top: var(--space-3);
        justify-content: flex-end;
      }

      .legend-label {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .legend-scale {
        display: flex;
        gap: 2px;
      }

      .legend-block {
        width: 14px;
        height: 14px;
        border-radius: 2px;
      }

      /* ─── Performance Breakdown ─── */
      .perf-row {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: var(--space-3);
      }

      .perf-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .perf-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .perf-label {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        font-weight: var(--font-medium);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
      }

      .perf-value {
        font-family: var(--font-display);
        font-size: var(--text-xl);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
      }

      .perf-bar-track {
        height: 8px;
        background: var(--color-bg-inset);
        border-radius: var(--radius-full);
        overflow: hidden;
      }

      .perf-bar-fill {
        height: 100%;
        border-radius: var(--radius-full);
        transition: width var(--duration-slower) var(--ease-out);
      }

      /* ─── Responsive ─── */
      @media (max-width: 1200px) {
        .kpi-grid {
          grid-template-columns: repeat(3, 1fr);
        }
      }

      @media (max-width: 900px) {
        .kpi-grid {
          grid-template-columns: repeat(2, 1fr);
        }
        .two-col {
          grid-template-columns: 1fr;
        }
        .perf-row {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      @media (max-width: 640px) {
        .kpi-grid {
          grid-template-columns: 1fr;
        }
        .perf-row {
          grid-template-columns: 1fr;
        }
        .controls-row {
          flex-direction: column;
          align-items: stretch;
        }
      }
    `
  ];

  constructor() {
    super();
    this.timeRange = '7d';
    this.agentFilter = 'all';
    this.hoveredBar = -1;
    this.hoveredHeatCell = null;

    this._initMockData();
  }

  /* ───────────────────────────────────────────
     Mock Data
  ─────────────────────────────────────────── */
  _initMockData() {
    this._kpis = [
      {
        label: 'Total Calls',
        value: '2,847',
        trend: +12.3,
        icon: 'fa-phone',
        accent: 'var(--color-primary)',
        bg: 'var(--color-primary-light)',
        sparkHeights: [40, 55, 48, 70, 62, 85, 78],
      },
      {
        label: 'Answer Rate',
        value: '94.2%',
        trend: +2.1,
        icon: 'fa-phone-arrow-up-right',
        accent: 'var(--color-success)',
        bg: 'var(--color-success-light)',
        sparkHeights: [70, 68, 75, 80, 78, 88, 94],
      },
      {
        label: 'Avg Handle Time',
        value: '4:32',
        trend: -5.4,
        icon: 'fa-stopwatch',
        accent: 'var(--color-accent)',
        bg: 'var(--color-accent-light)',
        sparkHeights: [82, 78, 72, 65, 68, 58, 52],
      },
      {
        label: 'CSAT',
        value: '4.7 / 5',
        trend: +3.2,
        icon: 'fa-face-smile',
        accent: 'var(--color-info)',
        bg: 'var(--color-info-light)',
        sparkHeights: [60, 65, 72, 70, 78, 82, 90],
      },
      {
        label: 'First Call Resolution',
        value: '87.5%',
        trend: +4.8,
        icon: 'fa-bullseye-arrow',
        accent: '#06B6D4',
        bg: '#ECFEFF',
        sparkHeights: [55, 62, 70, 68, 75, 82, 88],
      },
      {
        label: 'Conversion Rate',
        value: '23.4%',
        trend: +8.1,
        icon: 'fa-arrow-trend-up',
        accent: '#D946EF',
        bg: '#FDF4FF',
        sparkHeights: [30, 38, 42, 50, 55, 62, 70],
      },
    ];

    // Call volume: 24 bars
    this._callVolume = Array.from({ length: 24 }, (_, i) => {
      // Simulate realistic pattern: low at night, peaks in afternoon
      const base = i < 6 ? 8 : i < 9 ? 25 : i < 12 ? 55 : i < 15 ? 70 : i < 18 ? 60 : i < 21 ? 40 : 15;
      return {
        hour: i,
        label: i === 0 ? '12a' : i < 12 ? `${i}a` : i === 12 ? '12p' : `${i - 12}p`,
        calls: base + Math.floor(Math.random() * 20),
      };
    });
    this._callVolumeMax = Math.max(...this._callVolume.map(d => d.calls));

    // Funnel
    this._funnel = [
      { stage: 'Calls Received', count: 2847, pct: 100 },
      { stage: 'Answered', count: 2682, pct: 94.2 },
      { stage: 'Qualified', count: 1876, pct: 65.9 },
      { stage: 'Reservation Made', count: 667, pct: 23.4 },
      { stage: 'Payment Completed', count: 534, pct: 18.8 },
    ];

    // Heatmap: 7 days x 24 hours
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    this._heatmap = days.map(day => ({
      day,
      hours: Array.from({ length: 24 }, (_, h) => {
        // Realistic: low at night, high mid-day, weekends slightly different
        const isWeekend = day === 'Sat' || day === 'Sun';
        const base = h < 6 ? 0 : h < 9 ? (isWeekend ? 1 : 2) : h < 12 ? (isWeekend ? 3 : 4) : h < 15 ? 5 : h < 18 ? (isWeekend ? 4 : 3) : h < 21 ? 2 : 1;
        const jitter = Math.floor(Math.random() * 2);
        const intensity = Math.min(5, Math.max(0, base + jitter - 1));
        const calls = intensity * 8 + Math.floor(Math.random() * 10);
        return { hour: h, intensity, calls };
      }),
    }));

    // Performance metrics
    this._performance = [
      { label: 'Agent Utilization', value: '87%', pct: 87, color: 'var(--color-primary)' },
      { label: 'Quality Score', value: '92%', pct: 92, color: 'var(--color-success)' },
      { label: 'CSAT', value: '94%', pct: 94, color: 'var(--color-accent)' },
      { label: 'Resolution Rate', value: '88%', pct: 88, color: 'var(--color-info)' },
    ];
  }

  /* ───────────────────────────────────────────
     Handlers
  ─────────────────────────────────────────── */
  _setTimeRange(range) {
    this.timeRange = range;
  }

  _onAgentChange(e) {
    this.agentFilter = e.target.value;
  }

  _refresh() {
    // Re-generate some mock jitter for demo
    this._callVolume = this._callVolume.map(d => ({
      ...d,
      calls: d.calls + Math.floor(Math.random() * 10) - 5,
    }));
    this._callVolumeMax = Math.max(...this._callVolume.map(d => d.calls));
    this.requestUpdate();
  }

  /* ───────────────────────────────────────────
     Render
  ─────────────────────────────────────────── */
  render() {
    return html`
      ${iconStyles}
      <div class="dashboard-layout">

        <!-- Time Range Selector Row -->
        <div class="controls-row">
          <div class="controls-left">
            <div class="time-pills">
              ${['24h', '7d', '30d', '90d'].map(r => html`
                <button
                  class="time-pill ${this.timeRange === r ? 'active' : ''}"
                  @click="${() => this._setTimeRange(r)}"
                >${r.toUpperCase()}</button>
              `)}
            </div>

            <select class="agent-select" .value="${this.agentFilter}" @change="${this._onAgentChange}">
              <option value="all">All Agents</option>
              <option value="ava">Ava (AI)</option>
              <option value="noah">Noah (AI)</option>
              <option value="mia">Mia (AI)</option>
              <option value="liam">Liam (AI)</option>
            </select>
          </div>

          <button class="refresh-btn" @click="${this._refresh}" title="Refresh data">
            <i class="fa-sharp fa-light fa-arrows-rotate"></i>
          </button>
        </div>

        <!-- 6 KPI Cards -->
        <div class="kpi-grid">
          ${this._kpis.map(kpi => this._renderKPI(kpi))}
        </div>

        <!-- Call Volume Trend Chart -->
        <div class="section-card">
          <div class="section-header">
            <div class="section-title-group">
              <span class="section-title">Call Volume Trend</span>
              <span class="section-subtitle">Hourly call distribution for selected period</span>
            </div>
          </div>
          ${this._renderBarChart()}
        </div>

        <!-- Two-column: Funnel + Heatmap -->
        <div class="two-col">
          <!-- Conversion Funnel -->
          <div class="section-card">
            <div class="section-header">
              <div class="section-title-group">
                <span class="section-title">Conversion Funnel</span>
                <span class="section-subtitle">Call-to-payment pipeline</span>
              </div>
            </div>
            ${this._renderFunnel()}
          </div>

          <!-- Peak Hours Heatmap -->
          <div class="section-card">
            <div class="section-header">
              <div class="section-title-group">
                <span class="section-title">Peak Hours Heatmap</span>
                <span class="section-subtitle">Call intensity by day and hour</span>
              </div>
            </div>
            ${this._renderHeatmap()}
          </div>
        </div>

        <!-- Performance Breakdown Row -->
        <div class="section-card">
          <div class="section-header">
            <div class="section-title-group">
              <span class="section-title">Performance Breakdown</span>
              <span class="section-subtitle">Key operational metrics</span>
            </div>
          </div>
          <div class="perf-row">
            ${this._performance.map(m => this._renderPerfCard(m))}
          </div>
        </div>

      </div>
    `;
  }

  /* ───────────────────────────────────────────
     KPI Card
  ─────────────────────────────────────────── */
  _renderKPI(kpi) {
    const isUp = kpi.trend >= 0;
    return html`
      <div class="kpi-card" style="--kpi-accent: ${kpi.accent}; --kpi-bg: ${kpi.bg};">
        <div class="kpi-top">
          <div class="kpi-icon">
            <i class="fa-sharp-duotone fa-solid ${kpi.icon}"></i>
          </div>
          <span class="kpi-trend ${isUp ? 'up' : 'down'}">
            <i class="fa-sharp fa-light ${isUp ? 'fa-arrow-up' : 'fa-arrow-down'}" style="font-size: 9px;"></i>
            ${Math.abs(kpi.trend)}%
          </span>
        </div>
        <span class="kpi-value">${kpi.value}</span>
        <span class="kpi-label">${kpi.label}</span>
        <div class="kpi-sparkline">
          ${kpi.sparkHeights.map(h => html`
            <div class="spark-bar" style="height: ${h}%;"></div>
          `)}
        </div>
      </div>
    `;
  }

  /* ───────────────────────────────────────────
     Bar Chart
  ─────────────────────────────────────────── */
  _renderBarChart() {
    const max = this._callVolumeMax;
    return html`
      <div class="bar-chart">
        <div class="bar-chart-area">
          ${this._callVolume.map(d => {
            const pct = max > 0 ? (d.calls / max) * 100 : 0;
            return html`
              <div class="bar-column">
                <div class="bar-tooltip">${d.calls} calls</div>
                <div class="bar-fill" style="height: ${pct}%;"></div>
              </div>
            `;
          })}
        </div>
        <div class="bar-chart-x-axis">
          ${this._callVolume.map((d, i) => html`
            <span class="x-label ${i % 3 === 0 ? 'highlight' : ''}">${i % 2 === 0 ? d.label : ''}</span>
          `)}
        </div>
      </div>
    `;
  }

  /* ───────────────────────────────────────────
     Conversion Funnel
  ─────────────────────────────────────────── */
  _renderFunnel() {
    const maxCount = this._funnel[0].count;
    return html`
      <div class="funnel-stages">
        ${this._funnel.map((stage, idx) => {
          const widthPct = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
          return html`
            ${idx > 0 ? html`
              <div class="funnel-connector">
                <i class="fa-sharp fa-light fa-chevron-down"></i>
              </div>
            ` : ''}
            <div class="funnel-stage">
              <div class="funnel-bar-wrap">
                <div class="funnel-bar" style="width: ${widthPct}%;">
                  <span class="funnel-stage-label">${stage.stage}</span>
                  <span class="funnel-stage-count">${stage.count.toLocaleString()}</span>
                </div>
              </div>
              <span class="funnel-pct">${stage.pct}%</span>
            </div>
          `;
        })}
      </div>
    `;
  }

  /* ───────────────────────────────────────────
     Peak Hours Heatmap
  ─────────────────────────────────────────── */
  _renderHeatmap() {
    const hours = Array.from({ length: 24 }, (_, i) =>
      i === 0 ? '12a' : i < 12 ? `${i}a` : i === 12 ? '12p' : `${i - 12}p`
    );

    return html`
      <div class="heatmap-container">
        <div class="heatmap-grid">
          <!-- Column headers -->
          <div class="heatmap-corner"></div>
          ${hours.map(h => html`<div class="heatmap-col-header">${h}</div>`)}

          <!-- Rows -->
          ${this._heatmap.map(row => html`
            <div class="heatmap-row-label">${row.day}</div>
            ${row.hours.map(cell => html`
              <div class="heat-cell i${cell.intensity}" title="${row.day} ${hours[cell.hour]}: ${cell.calls} calls">
                <span class="heat-cell-tooltip">${cell.calls}</span>
              </div>
            `)}
          `)}
        </div>

        <!-- Legend -->
        <div class="heatmap-legend">
          <span class="legend-label">Low</span>
          <div class="legend-scale">
            <div class="legend-block" style="background: var(--color-bg-tertiary);"></div>
            <div class="legend-block" style="background: rgba(45, 143, 111, 0.12);"></div>
            <div class="legend-block" style="background: rgba(45, 143, 111, 0.28);"></div>
            <div class="legend-block" style="background: rgba(45, 143, 111, 0.45);"></div>
            <div class="legend-block" style="background: rgba(45, 143, 111, 0.65);"></div>
            <div class="legend-block" style="background: rgba(45, 143, 111, 0.85);"></div>
          </div>
          <span class="legend-label">High</span>
        </div>
      </div>
    `;
  }

  /* ───────────────────────────────────────────
     Performance Card
  ─────────────────────────────────────────── */
  _renderPerfCard(m) {
    return html`
      <div class="perf-card">
        <div class="perf-header">
          <span class="perf-label">${m.label}</span>
        </div>
        <span class="perf-value">${m.value}</span>
        <div class="perf-bar-track">
          <div class="perf-bar-fill" style="width: ${m.pct}%; background: ${m.color};"></div>
        </div>
      </div>
    `;
  }
}

customElements.define('analytics-dashboard', AnalyticsDashboard);
